import Breadcrumbs from 'components/Breadcrumbs'
import DateFilter from 'components/DateFilter'
import PlayerFilter from 'components/PlayerFilter'
import ProgressChart from 'components/ProgressChart'
import RecentSessions from 'components/RecentSessions'
import StatCard from 'components/StatCard'
import { fetchBankroll } from 'config/api'
import routes from 'config/routes'
import { CurrencyContext } from 'contexts'
import { useBankrolls } from 'hooks'
import React from 'react'
import { Card, Col, Row, Collapse } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { useToasts } from 'react-toast-notifications'
import StatsTable from 'components/StatsTable'
import BankrollFilter from 'components/BankrollFilter'

function Dashboard() {
  const currency = React.useContext(CurrencyContext)
  const {
    currentBankroll,
    bankrolls,
    selectBankroll,
    createBankroll,
    players,
  } = useBankrolls()
  const { addToast } = useToasts()
  const [showStats, setShowStats] = React.useState(false)
  const [xAxis, setXAxis] = React.useState('sessions')
  const [playerFilter, setPlayerFilter] = React.useState(null)
  const [dateFilters, setDateFilters] = React.useState({
    date__gte: null,
    date__lte: null,
  })

  const qBankroll = useQuery(
    ['bankrolls', currentBankroll.id, { playerFilter, dateFilters }],
    ({ queryKey }) => {
      const [, , { playerFilter, dateFilters }] = queryKey
      return fetchBankroll(currentBankroll.id, {
        player: playerFilter,
        ...dateFilters,
      })
    },
    {
      onError: err => {
        addToast('Error retrieving bankroll', {
          appearance: 'error',
          autoDismiss: true,
        })
        console.error('Error retrieving bankroll')
        console.error(err)
      },
    },
  )

  const handleDateFilter = React.useCallback((start, end) => {
    setDateFilters({
      date__gte: start,
      date__lte: end,
    })
  }, [])

  const handlePlayerFilter = React.useCallback(setPlayerFilter, [])

  const {
    earnings,
    earnings_data,
    ev_data,
    av,
    hours,
    hours_data,
    recent_sessions,
    expenses,
    include_expenses,
  } = {
    ...currentBankroll,
    ...(qBankroll.isSuccess ? qBankroll.data : {}),
  }

  const chartSeries = React.useMemo(() => {
    const earningsSeries = []
    const evSeries = []
    for (let i in earnings_data) {
      const x = xAxis === 'sessions' ? i : hours_data[i]
      earningsSeries.push({ x, y: earnings_data[i] })
      evSeries.push({ x, y: ev_data[i] })
    }
    return currentBankroll.ev_disabled
      ? [earningsSeries]
      : [earningsSeries, evSeries]
  }, [currentBankroll.ev_disabled, earnings_data, ev_data, hours_data, xAxis])

  return (
    <div id="dashboard">
      <Breadcrumbs crumbs={[['DASHBOARD', routes.dashboard]]} />
      <Row>
        <Col className="d-flex flex-wrap justify-content-start justify-content-md-between">
          <BankrollFilter
            current={currentBankroll}
            choices={bankrolls}
            onChange={selectBankroll}
            onCreate={createBankroll}
          />
          <div>
            <DateFilter
              disabled={qBankroll.isLoading}
              onChange={handleDateFilter}
            />
            {currentBankroll.shared_users.length > 0 && (
              <PlayerFilter
                disabled={qBankroll.isLoading}
                onChange={handlePlayerFilter}
                choices={players}
              />
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col className="mt-3" md={12} lg={4}>
          <StatCard
            title={
              include_expenses ? (
                <>
                  Earnings{' '}
                  <small className="text-lowercase">(minus expenses)</small>
                </>
              ) : (
                'Total Earnings'
              )
            }
            value={
              include_expenses
                ? parseFloat(earnings) - parseFloat(expenses)
                : earnings
            }
            prefix={currency.icon}
            integer
          />
        </Col>
        <Col className="mt-3" md>
          <StatCard
            title="AV"
            value={av}
            prefix={currency.icon}
            unit="/hr"
            integer
          />
        </Col>
        <Col className="mt-3" md>
          <StatCard title="Hours Played" integer value={hours} />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="mt-3 d-flex justify-content-end">
            <button
              className="btn btn-link text-uppercase"
              type="button"
              onClick={() => setShowStats(!showStats)}
            >
              {showStats ? 'Fewer Stats' : 'More Stats'}
            </button>
          </div>
          <Collapse in={showStats}>
            <div>
              <Card className="stat-card shadow h-100 mt-3">
                <Card.Body>
                  <Card.Title className="text-uppercase">
                    Additional Stats
                  </Card.Title>
                  <StatsTable bankroll={currentBankroll} />
                </Card.Body>
              </Card>
            </div>
          </Collapse>
        </Col>
      </Row>
      <Row>
        <Col lg={7} className="mt-3">
          <ProgressChart
            series={chartSeries}
            xAxis={xAxis}
            onToggleXAxis={setXAxis}
          />
        </Col>
        <Col className="mt-3">
          <RecentSessions sessions={recent_sessions} />
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard
